import { useQuery } from '@tanstack/react-query'
import { Control, FieldPath, FieldValues } from 'react-hook-form'

import { fetchBridgeReferrals } from '@/api/modules'
import GradientWrapper from '@/common/GradientWrapper'
import { Referral } from '@/generated/proto/bridge/models/referrals'
import { formatAddress } from '@/helpers/formatters'
import { queryKeys } from '@/queries/keys'
import {
  ControlledUiSelect,
  UiSelectContent,
  UiSelectItem,
  UiSelectTrigger,
  UiSelectValue,
} from '@/ui/UiSelect'

interface ReferralSelectProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  disabled?: boolean
  placeholder?: string
  onReferralSelect?: (referral: Referral) => void
  onValueChange?: (referralId: string) => void
}

export default function ReferralSelect<TFieldValues extends FieldValues>({
  control,
  name,
  onValueChange,
  onReferralSelect,
  disabled = false,
  placeholder = 'Select a referral',
}: ReferralSelectProps<TFieldValues>) {
  const { data: referrals = [], isLoading: loadingReferrals } = useQuery({
    queryKey: queryKeys.referrals.list(),
    queryFn: fetchBridgeReferrals,
  })

  return (
    <ControlledUiSelect
      name={name}
      control={control}
      onValueChange={referralId => {
        onValueChange?.(referralId)
        const selectedReferral = referrals.find(
          referral => referral.id === Number(referralId),
        )
        if (selectedReferral) {
          onReferralSelect?.(selectedReferral)
        }
      }}
      disabled={disabled || loadingReferrals}
    >
      <GradientWrapper>
        <UiSelectTrigger
          size='xl'
          className='bg-input border-border w-full rounded-lg border px-3 py-2 text-sm outline-none disabled:opacity-60'
        >
          <UiSelectValue
            placeholder={loadingReferrals ? 'Loading...' : placeholder}
          />
        </UiSelectTrigger>
      </GradientWrapper>

      <UiSelectContent>
        {referrals.map(referral => (
          <UiSelectItem key={referral.id} value={String(referral.id)}>
            <div className='flex w-full flex-col gap-1 rounded-md transition-colors md:flex-row md:items-center md:gap-4'>
              <div className='text-muted-foreground text-xs md:text-sm'>
                <span className='md:hidden'>ID </span>
                <strong>#{referral.id}</strong>
              </div>

              <div className='text-foreground font-mono text-xs md:flex-1 md:text-sm'>
                {formatAddress(referral.withdrawalAddress, 16, 8)}
              </div>

              <div className='text-primary text-xs font-semibold md:text-right md:text-sm'>
                {referral.commissionRate}%
              </div>
            </div>
          </UiSelectItem>
        ))}
      </UiSelectContent>
    </ControlledUiSelect>
  )
}
